import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { WIKI_MODULES } from '../data/wikiContent';
import { Search, BookOpen, ChevronLeft, ChevronRight, Info, AlertTriangle } from 'lucide-react';

// ─── Inline **bold** renderer ─────────────────────────────────────────────────
function Rich({ text }) {
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((p, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold text-soem-navy">{p}</strong> : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

// ─── Block renderer ───────────────────────────────────────────────────────────
function Block({ block }) {
  switch (block.t) {
    case 'h':
      return <h3 className="text-base font-bold text-soem-navy mt-6 mb-2"><Rich text={block.text} /></h3>;
    case 'p':
      return <p className="text-sm text-slate-600 leading-relaxed mb-3"><Rich text={block.text} /></p>;
    case 'steps':
      return (
        <ol className="list-decimal pl-5 space-y-1.5 mb-3">
          {block.items.map((it, i) => <li key={i} className="text-sm text-slate-600 leading-relaxed"><Rich text={it} /></li>)}
        </ol>
      );
    case 'list':
      return (
        <ul className="list-disc pl-5 space-y-1.5 mb-3">
          {block.items.map((it, i) => <li key={i} className="text-sm text-slate-600 leading-relaxed"><Rich text={it} /></li>)}
        </ul>
      );
    case 'tip':
      return (
        <div className="flex gap-2.5 bg-blue-50 border border-blue-100 rounded-sm px-4 py-3 mb-3">
          <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-900 leading-relaxed"><Rich text={block.text} /></p>
        </div>
      );
    case 'warn':
      return (
        <div className="flex gap-2.5 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 mb-3">
          <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 leading-relaxed"><Rich text={block.text} /></p>
        </div>
      );
    case 'flow':
      return (
        <pre className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 mb-3 text-xs text-soem-navy font-mono whitespace-pre overflow-x-auto">{block.text}</pre>
      );
    case 'table':
      return (
        <div className="overflow-x-auto mb-3 border border-slate-200 rounded-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {block.headers.map((h, i) => (
                  <th key={i} className="text-left px-4 py-2.5 font-semibold text-soem-navy text-xs uppercase tracking-wide"><Rich text={h} /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 ? 'bg-slate-50/50' : ''}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-2.5 text-slate-600 align-top border-t border-slate-100"><Rich text={cell} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  lider_proyecto: 'Líder',
  levantador: 'Levantador',
  cliente: 'Cliente',
};

// Text of a module flattened for search matching.
function moduleText(m) {
  const parts = [m.title, m.summary];
  for (const b of m.blocks) {
    if (b.text) parts.push(b.text);
    if (b.items) parts.push(...b.items);
    if (b.headers) parts.push(...b.headers);
    if (b.rows) for (const r of b.rows) parts.push(...r);
  }
  return parts.join(' ').toLowerCase();
}

export default function Wiki() {
  const { activeRole } = useAuth();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const contentRef = useRef(null);

  const visibleModules = useMemo(() => {
    let mods = WIKI_MODULES;
    if (!showAll && activeRole) {
      const forRole = mods.filter(m => m.roles.includes(activeRole));
      // Fallback: si el rol no matchea nada (no debería pasar), muestra todo.
      if (forRole.length > 0) mods = forRole;
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      mods = mods.filter(m => moduleText(m).includes(s));
    }
    return mods;
  }, [activeRole, showAll, search]);

  // Keep a valid selection when the visible list changes.
  useEffect(() => {
    if (visibleModules.length === 0) return;
    if (!visibleModules.some(m => m.id === activeId)) {
      setActiveId(visibleModules[0].id);
    }
  }, [visibleModules, activeId]);

  const activeIndex = visibleModules.findIndex(m => m.id === activeId);
  const activeModule = activeIndex >= 0 ? visibleModules[activeIndex] : null;
  const prevModule = activeIndex > 0 ? visibleModules[activeIndex - 1] : null;
  const nextModule = activeIndex >= 0 && activeIndex < visibleModules.length - 1 ? visibleModules[activeIndex + 1] : null;

  const selectModule = (id) => {
    setActiveId(id);
    // Volver el contenido al inicio al cambiar de módulo.
    if (contentRef.current) contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wiki-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy flex items-center gap-2">
            <BookOpen size={24} className="text-soem-primary" /> Wiki SOEM
          </h1>
          <p className="text-slate-500 text-sm mt-1">Cómo funciona la plataforma, explicado módulo por módulo</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showAll}
            onChange={e => setShowAll(e.target.checked)}
            className="accent-soem-primary"
            data-testid="wiki-show-all-toggle"
          />
          Ver todos los módulos
        </label>
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="pl-10 pr-4 py-3 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
          placeholder="Buscar en la wiki... (ej. bloqueante, QR, plantilla, mapa)"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Buscar en la wiki"
          data-testid="wiki-search"
        />
      </div>

      {visibleModules.length === 0 ? (
        <div className="soem-card p-12 text-center">
          <BookOpen size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Sin resultados para tu búsqueda.</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Índice de módulos */}
          <nav className="w-full lg:w-64 flex-shrink-0 lg:sticky lg:top-4" aria-label="Módulos de la wiki">
            <div className="soem-card p-2 space-y-0.5 max-h-none lg:max-h-[70vh] overflow-y-auto">
              {visibleModules.map(m => {
                const Icon = m.icon;
                const isActive = m.id === activeId;
                return (
                  <button
                    key={m.id}
                    onClick={() => selectModule(m.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-left text-sm transition-colors ${
                      isActive ? 'bg-soem-primary text-white font-semibold' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`wiki-nav-${m.id}`}
                  >
                    <Icon size={16} strokeWidth={1.5} className="flex-shrink-0" />
                    <span className="truncate">{m.title}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Contenido del módulo */}
          {activeModule && (
            <article ref={contentRef} className="flex-1 min-w-0 soem-card p-6 sm:p-8" data-testid={`wiki-module-${activeModule.id}`}>
              <div className="border-b border-slate-100 pb-4 mb-4">
                <h2 className="text-xl font-bold text-soem-navy flex items-center gap-2.5">
                  <activeModule.icon size={22} className="text-soem-primary" strokeWidth={1.5} />
                  {activeModule.title}
                </h2>
                <p className="text-sm text-slate-500 mt-1">{activeModule.summary}</p>
                <div className="flex gap-1.5 mt-3 flex-wrap">
                  {activeModule.roles.map(r => (
                    <span key={r} className="text-[10px] uppercase tracking-wide font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                      {ROLE_LABELS[r] || r}
                    </span>
                  ))}
                </div>
              </div>

              {activeModule.blocks.map((b, i) => <Block key={i} block={b} />)}

              {/* Navegación anterior / siguiente */}
              <div className="flex items-center justify-between gap-3 mt-8 pt-4 border-t border-slate-100">
                {prevModule ? (
                  <button
                    onClick={() => selectModule(prevModule.id)}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-soem-primary transition-colors"
                    data-testid="wiki-prev-btn"
                  >
                    <ChevronLeft size={16} /> {prevModule.title}
                  </button>
                ) : <span />}
                {nextModule ? (
                  <button
                    onClick={() => selectModule(nextModule.id)}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-soem-primary transition-colors text-right"
                    data-testid="wiki-next-btn"
                  >
                    {nextModule.title} <ChevronRight size={16} />
                  </button>
                ) : <span />}
              </div>
            </article>
          )}
        </div>
      )}
    </div>
  );
}
